import {
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Button,
} from "antd";
import { useEffect, useState } from "react";
import api from "../../api/axios";
import { fetchAccounts } from "../../redux/slices/accountsSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { fetchResellers } from "../../redux/slices/resellerSlice";

interface ResellerFormModalProps {
  visible: boolean;
  onClose: () => void;
  editingReseller: any | null;
}

export function ResellerFormModal({
  visible,
  onClose,
  editingReseller,
}: ResellerFormModalProps) {
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const { accounts } = useSelector((state: RootState) => state.accounts);
  const { resellers } = useSelector((state: RootState) => state.resellers);

  const [saving, setSaving] = useState(false);

  // ✅ NEW: force reset password state
  const [forcePassword, setForcePassword] = useState("");
  const [forceResetLoading, setForceResetLoading] = useState(false);

  useEffect(() => {
    if (editingReseller) {
      form.setFieldsValue(editingReseller);
    } else {
      form.resetFields();
      form.setFieldsValue({
        isEsimReseller: true,
      });
    }

    // ✅ clear force password box when modal opens / changes record
    setForcePassword("");
  }, [editingReseller, form]);

  useEffect(() => {
    dispatch(fetchAccounts());
  }, [dispatch]);

  // ✅ NEW: call backend endpoint to reset reseller password
  const handleForceResetPassword = async () => {
    const resellerId = editingReseller?.resellerId;

    if (!resellerId) {
      message.error("Missing resellerId");
      return;
    }

    if (!forcePassword || forcePassword.trim().length < 8) {
      message.error("Password must be at least 8 characters");
      return;
    }

    setForceResetLoading(true);
    try {
      await api.post("/pos/Admin/resellers/reset-password", {
        resellerId,
        newPassword: forcePassword.trim(),
      });
      message.success("Password reset successfully");
      setForcePassword("");
      await dispatch(fetchResellers());
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to reset password";
      message.error(msg);
    } finally {
      setForceResetLoading(false);
    }
  };

  const handleFinish = async (values: any) => {
    const username = (values.username || "").trim();
    const email = (values.email || "").trim();
    const firstName = (values.firstName || "").trim();
    const lastName = (values.lastName || "").trim();

    // client-side duplicate guard to avoid backend 500 on existing username
    const normalizedUsername = username.toLowerCase();
    const editingUsername =
      (editingReseller?.username || "").trim().toLowerCase();
    if (
      normalizedUsername &&
      normalizedUsername !== editingUsername &&
      resellers.some(
        (r: any) =>
          (r.username || "").trim().toLowerCase() === normalizedUsername
      )
    ) {
      message.error("Username already exists. Please choose another.");
      return;
    }

    setSaving(true);
    try {
      const fullPayload = {
        ...editingReseller,
        ...values,
        username,
        email,
        firstName,
        lastName,
        name: `${firstName}${lastName ? ` ${lastName}` : ""}` || username,
        isAdmin: values.isAdmin ?? editingReseller?.isAdmin ?? false,
        isEsimReseller:
          values.isEsimReseller ?? editingReseller?.isEsimReseller ?? true,
        resellerId: values.resellerId ?? editingReseller?.resellerId ?? null,
        stockAccountId:
          values.stockAccountId ?? editingReseller?.stockAccountId ?? null,
        salesAccountId:
          values.salesAccountId ?? editingReseller?.salesAccountId ?? null,
      };

      // avoid sending empty password on edit
      if (!values.password) {
        delete (fullPayload as any).password;
      }

      await api.post("/User/register", fullPayload, { validateStatus: () => true });
      message.success("Reseller saved successfully");
      await dispatch(fetchResellers());
      onClose();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save reseller";
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={visible}
      title={editingReseller ? "Edit Reseller" : "Create Reseller"}
      onCancel={onClose}
      onOk={() => form.submit()}
      destroyOnClose
      confirmLoading={saving}
      okButtonProps={{ disabled: saving }}
      cancelButtonProps={{ disabled: saving }}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="username"
              label="Username"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: !editingReseller, min: 6 }]}
            >
              <Input.Password />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true, type: "email" }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item name="stockAccountId" label="Stock Account">
              <Select allowClear>
                {accounts.map((acc) => (
                  <Select.Option key={acc.id} value={acc.id}>
                    {acc.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="balance"
              label="Balance"
              rules={[{ required: true, message: "Please input balance" }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item name="salesAccountId" label="Sales Account">
              <Select allowClear>
                {accounts.map((acc) => (
                  <Select.Option key={acc.id} value={acc.id}>
                    {acc.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} style={{ display: "flex", flexDirection: "column" }}>
            <Form.Item name="isAdmin" valuePropName="checked" style={{ margin: 0 }}>
              <Checkbox>Is Admin</Checkbox>
            </Form.Item>
            <Form.Item name="isEsimReseller" valuePropName="checked" style={{ margin: 0 }}>
              <Checkbox>Is Esim Reseller</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        {/* ✅ NEW: Force Reset Password (only in Edit mode) */}
        {editingReseller?.resellerId && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #eee" }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Force reset password
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <Input.Password
                placeholder="New password (min 8 chars)"
                value={forcePassword}
                onChange={(e) => setForcePassword(e.target.value)}
                disabled={saving || forceResetLoading}
              />
              <Button
                type="primary"
                danger
                loading={forceResetLoading}
                disabled={saving}
                onClick={handleForceResetPassword}
              >
                Reset
              </Button>
            </div>
          </div>
        )}
      </Form>
    </Modal>
  );
}
